// this loads up segment analytics types
/// <reference types="@types/segment-analytics" />
import JsCookies from 'js-cookie';
import axios from 'axios';
import { pick } from 'lodash';
import logger from 'logger';
import { getNextConfigValues } from 'next-config';
import ConfigContext from 'src/context/config';
import UserContext from 'src/context/user';
import { GET_USER } from 'src/graphql-operations/rails/user.query';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { initializeApollo } from 'src/utils/apolloClient';
import {
  ANONYMOUS_ID_COOKIE,
  ENVIRONMENTS,
  RailsCookies,
} from 'src/utils/constants';
import { isServerSide } from 'src/utils/next';
import { v4 as uuidv4 } from 'uuid';

import { UserQuery } from '../ts/@types/generated/graphql/rails/graphql';
import {
  SegmentServerSideIdentifyRequest,
  SegmentServerSidePageRequest,
  SegmentServerSideTrackRequest,
} from './interfaces';

// augment global Window object to include the values we access for Segment
declare global {
  interface Window {
    adsEnabled?: boolean;
    segmentLoaded?: boolean;
    relicxSDK?: any;
  }
}

const { publicRuntimeConfig } = getNextConfigValues();

const isProd = publicRuntimeConfig.ENV === ENVIRONMENTS.production;
const isStg = publicRuntimeConfig.ENV === ENVIRONMENTS.staging;

/**
 * Get stored id if exists; if not create a new one and store.
 *
 * Return either the existing id or the newly created one.
 */
const checkSetAnonId = (): string => {
  const anonymousId = JsCookies.get(ANONYMOUS_ID_COOKIE) ?? uuidv4();
  // we only need to set this cookie manually if segment doesn't load
  if (!window.segmentLoaded) JsCookies.set(ANONYMOUS_ID_COOKIE, anonymousId);
  return anonymousId;
};

type SegmentIdentifyHookCall = (
  options?: SegmentAnalytics.SegmentOpts,
  callback?: () => void,
  extraTraits?: Record<string, unknown>,
  email?: string,
) => void;

type SegmentPageHookCall = (
  name?: string,
  properties?: Record<string, unknown>,
) => void;

type SegmentTrackFunction = <P extends Record<string, unknown>>(
  event: string,
  properties?: P,
  options?: SegmentAnalytics.SegmentOpts,
  callback?: () => void,
) => void;

const SEGMENT_VALID_USER_TRAIT_NAMES = [
  'id',
  'firstName',
  'lastName',
  'name',
  'email',
  'phone',
  'tenantId',
] as const;

/**
 * Takes in possibly undefined userId; if defined, set to cookie and return provided value;
 * otherwise, try to get from cookie store.
 *
 * userId from cookie store is thus used as backup when userId is not available directly already
 * via context or function arguments
 */
const setAndGetUserId = (userId: string | undefined): string | undefined => {
  if (userId) {
    JsCookies.set(RailsCookies.USER_ID, userId);
    return userId;
  }
  return JsCookies.get(RailsCookies.USER_ID);
};

/**
 * Helper for short circuiting analytics functions -- should only fire if client side
 * and segment is enabled.
 */
const shouldShortCircuit = (segmentEnabled: boolean): boolean =>
  isServerSide() || !segmentEnabled;

// Type is little complicated but gives us gaurdrail that the object we create
// should have keys matching what we'll extract as valid with the pick() call below
// (ie save us from dropping off tenantId, or firstName, or something by mistake)
type ValidUserTraits = Pick<
  NonNullable<UserQuery['user']>,
  Exclude<(typeof SEGMENT_VALID_USER_TRAIT_NAMES)[number], 'tenantId'>
> & { tenantId: number };

/**
 * Hook to access functions for core segment analytics functionality.
 *
 * Leverages UserContext so that calls will automatically include user
 * info without having to call across the app.
 */
const useAnalytics = () => {
  const segmentProxyRoute = publicRuntimeConfig.segment.proxy;
  const config = useNonNullContext(ConfigContext);
  const analyticsKey = isProd
    ? config.clientSideKeys.segmentKey
    : config.clientSideKeys.segmentKeyStg;
  // just a check so that we don't send events from localhost which just clutters up the Segment data.
  const segmentEnabled = !!analyticsKey && (isProd || isStg);

  const { user, tenantId } = useNonNullContext(UserContext);
  const userTraits = { ...user, tenantId } as ValidUserTraits;
  const userId = setAndGetUserId(user?.id);

  const identify: SegmentIdentifyHookCall = async (
    options,
    callback,
    extraTraits,
    email,
  ) => {
    if (shouldShortCircuit(segmentEnabled)) return;

    const validTraits = pick(userTraits, SEGMENT_VALID_USER_TRAIT_NAMES);
    const anonymousId = checkSetAnonId();
    // Segment is going to automatically generate a UUID/cookie for us so we need
    // to explicitly tell it the correct id.
    if (options) {
      options.anonymousId = anonymousId.replace(/['"]+/g, '');
    }

    //We only want to identify a user when there's a userId or email to do it by (see PLAT-2679)
    if (userId || email) {
      const mergedTraits = {
        ...validTraits,
        // if email is passed in, we want to overwrite the email in the traits.
        //We do this to identify users by email on get-started
        ...(email && { email }),
        ...(extraTraits && extraTraits),
      };

      if (window?.adsEnabled && window?.segmentLoaded) {
        window.analytics &&
          window.analytics.identify &&
          window.analytics.identify(
            userId || '',
            mergedTraits,
            options || {},
            callback,
          );
        return;
      }
      const segmentServerIdentifyBody: SegmentServerSideIdentifyRequest = {
        userId: userId || '',
        tenantId,
        anonymousId,
        traits: mergedTraits,
      };

      try {
        await axios.post(
          `${segmentProxyRoute}/identify`,
          {
            ...segmentServerIdentifyBody,
          },
          { headers: { accept: 'application/json' } },
        );
        !!callback && callback();
      } catch (_err) {}
    }
  };

  const page: SegmentPageHookCall = async (name, properties) => {
    if (shouldShortCircuit(segmentEnabled)) return;
    let isMobile = false;
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      isMobile = true;
    }
    const allProperties = { ...properties, isMobile, tenantId };

    if (window?.adsEnabled && window?.segmentLoaded) {
      window.analytics &&
        window.analytics.page &&
        window.analytics.page(name, allProperties);
      return;
    }

    const {
      location: { pathname: path, href: fullUrl },
    } = window;
    const { title: pageTitle } = document;

    let userId = JsCookies.get(RailsCookies.USER_ID);
    const rememberUserToken = JsCookies.get(RailsCookies.REMEMBER_USER_TOKEN);
    const sessionId = JsCookies.get(RailsCookies.SESSION_ID);
    const queryParams = window.location.search;
    // try to query for userId for tracks if userId is not already set
    // using rails remember token and/or session id
    if (!userId && (rememberUserToken || sessionId)) {
      // this will always be client side call
      const apolloClient = initializeApollo({});
      try {
        const { data } = await apolloClient.query({
          query: GET_USER,
        });
        userId = data?.user?.id || '';

        if (userId) {
          JsCookies.set(RailsCookies.USER_ID, userId);
        }
      } catch (err: any) {
        logger.error({
          msg: 'Failed to get user for segment analytics',
          noticable: err,
        });
      }
    }

    const anonymousId = checkSetAnonId();

    const requestBody: SegmentServerSidePageRequest = {
      ...properties,
      userId: userId!,
      anonymousId,
      pageTitle,
      path,
      queryParams,
      fullUrl,
      isMobile,
      tenantId,
    };

    try {
      await axios.post(
        `${segmentProxyRoute}/page`,
        {
          ...requestBody,
        },
        { headers: { accept: 'application/json' } },
      );
    } catch (_err) {}
  };

  const track: SegmentTrackFunction = async (
    event,
    properties,
    options,
    callback,
  ) => {
    if (shouldShortCircuit(segmentEnabled)) return;

    const allProperties = { ...properties, tenantId };

    if (window?.adsEnabled && window?.segmentLoaded) {
      window.analytics &&
        window.analytics.track &&
        window.analytics.track(event, allProperties, options || {}, callback);
      return;
    }

    const anonymousId = checkSetAnonId();
    const userId =
      setAndGetUserId(user?.id) || (properties?.userId as string) || '';

    const segmentServerTrackBody: SegmentServerSideTrackRequest = {
      anonymousId,
      userId,
      tenantId,
      event,
      properties: properties || {},
    };

    try {
      await axios.post(
        `${segmentProxyRoute}/track`,
        {
          ...segmentServerTrackBody,
        },
        { withCredentials: true },
      );
    } catch (_err) {}
    !!callback && callback();
  };

  // NOTE: A timeout (sleep) after call to :load allows script to finish b/f calling :identify or :page.
  // We can't rely on our onload event in analytics.js to fire every time, because it could be blocked
  // and there's no equivalent callback for "on script blocked"
  const LOAD_DELAY = 750;
  const load = (): void => {
    if (shouldShortCircuit(segmentEnabled)) return;
    window.analytics &&
      window.analytics.load &&
      window.analytics.load(analyticsKey);
  };

  return { identify, page, track, LOAD_DELAY, load };
};

export default useAnalytics;
